import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  FONT_SIZES,
  GenericIconName,
  IconSize,
  SPACING,
  UnstyledButton,
  Z_INDEXES,
  mediaLargeScreens,
  useKeyPress,
} from '@aboutyou/ui-components';
import {css, cx} from '@linaria/core';
import {styled} from '@linaria/react';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  ConditionalSearchSuggestionsHistory,
  MIN_CHARS_TO_SEARCH,
} from '../../SearchSuggestions/ConditionalSearchSuggestionsHistory';
import {useSearchPanelPreload} from '../../SearchSuggestions/useSearchPanelPreload';
import {
  HEADER_DEFAULT_PADDING,
  SEARCH_BAR_ANIMATION_TIME_MS,
  SEARCH_BAR_FOCUSED_WIDTH,
  SEARCH_BAR_HEIGHT,
  SEARCH_BAR_PINNED_TOP,
  SEARCH_BAR_TABLET_WIDTH,
} from '../constants';
import {
  SearchBarWrapper,
  SearchBarWrapperTablet,
  StyledSearchIcon as SearchIcon,
  StyledInput as SearchInput,
  StyledCloseIcon,
  Wrapper,
} from './SearchBar.eager';
import {SearchBarPageOverlay} from './SearchBarPageOverlay';
import {useExpandToCenter} from './hooks/useExpandToCenter';
import {useSearchBar} from './hooks/useSearchBar';
import {SearchBarOpenOrigin} from './hooks/useSearchBarDisplayState';
import {useSearchSuggestions} from './hooks/useSearchSuggestions';

interface SearchBarProps {
  isSearchBarExpanded: boolean;
  expandSearchBar: VoidFunction;
  collapseSearchBar: VoidFunction;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchWrapperRef: React.RefObject<HTMLDivElement | null>;
  isSearchInTabletMode: boolean;
  className?: string;
  expandToCenter?: boolean;
  onExtendedChange?: (isExtended: boolean) => void;
  openOrigin?: SearchBarOpenOrigin | null;
}

export const SearchSuggestions: React.FC<SearchBarProps> = ({
  isSearchInTabletMode,
  isSearchBarExpanded,
  expandSearchBar,
  collapseSearchBar,
  searchInputRef,
  searchWrapperRef,
  className,
  expandToCenter,
  onExtendedChange,
  openOrigin,
}) => {
  const {t} = useTranslation();
  const {
    isInputFocused,
    searchValue,
    onFocusSearchInput,
    handleInputOnChange,
    handleRemoveInputFocus,
    closeSearchResult,
    handleOnClickSearchIcon,
    collapseAndBlurInput,
    setSuggestionSelected,
    suggestionSelected,
  } = useSearchBar({
    isSearchInTabletMode,
    isSearchBarExpanded,
    collapseSearchBar,
    expandSearchBar,
    searchInputRef,
    searchWrapperRef,
  });

  const {
    clearSearchAndFocus,
    createSearchPath,
    handleInputOnChangeWithDebounce,
    handleInputOnChangeTrackingWithDebounce,
    onClickSuggestion,
    onSearchInputEnter,
    shouldShowCloseIcon,
    clearButtonOnKeyDownHandler,
    tabletOpenButtonRef,
  } = useSearchSuggestions({
    collapseAndBlurInput,
    handleInputOnChange,
    handleRemoveInputFocus,
    searchInputRef,
    isSearchBarExpanded,
    isSearchInTabletMode,
    searchValue,
    searchWrapperRef,
  });

  useKeyPress({
    key: 'Enter',
    onKeyDown: onSearchInputEnter,
    disabled:
      !isInputFocused ||
      (searchValue.length >= MIN_CHARS_TO_SEARCH && suggestionSelected),
  });

  const isSearchBarExtended = isSearchInTabletMode
    ? isSearchBarExpanded
    : isInputFocused;

  // A pointer or focus reaching the bar is the earliest honest signal that the panel is
  // about to be needed. It never resets: the data is cached from here on, so a
  // second aim has nothing left to trigger.
  const [hasSearchIntent, setHasSearchIntent] = useState(false);
  const onSearchIntent = useCallback(() => {
    setHasSearchIntent(true);
  }, []);

  useSearchPanelPreload({shouldPreload: hasSearchIntent});

  const {
    canExpandToCenter,
    shouldExpandToCenter,
    hasMeasurement,
    centerOffset,
    isPinned,
    originOffset,
    originWidth,
  } = useExpandToCenter({
    searchWrapperRef,
    expandToCenter,
    isSearchBarExtended,
    openOrigin,
  });

  // Opened from the sticky header, the bar has an origin to grow out of. It is
  // derived rather than timed so the animation is already on the element when
  // the pinned bar is painted for the first time. Once it has grown it keeps
  // the animation for the rest of the open: dropping it while the bar scrolls
  // out of the pinned state would replay it on the way back in.
  const hasGrownOutOfIcon = useRef(false);
  if (!openOrigin) {
    hasGrownOutOfIcon.current = false;
  } else if (isPinned) {
    hasGrownOutOfIcon.current = true;
  }
  const isGrowingOutOfIcon = Boolean(openOrigin) && hasGrownOutOfIcon.current;

  // The header row renders outside this subtree but needs to know whether the
  // bar is open, to drop its own search icon while it is. Resetting on unmount
  // keeps that icon from staying hidden once the bar itself is gone.
  useEffect(() => {
    onExtendedChange?.(isSearchBarExtended);

    return () => {
      onExtendedChange?.(false);
    };
  }, [isSearchBarExtended, onExtendedChange]);

  return (
    <Wrapper
      ref={searchWrapperRef}
      data-testid="searchBar"
      className={className}
    >
      {shouldExpandToCenter && hasMeasurement && (
        <SearchBarPageOverlay onDismiss={closeSearchResult} />
      )}
      <StyledSearchBarWrapper
        onPointerEnter={onSearchIntent}
        onFocus={onSearchIntent}
        className={getSearchBarModifierClasses({
          isSearchBarExpanded,
          isSearchInTabletMode,
          isInputFocused,
          isSearchBarExtended,
          canExpandToCenter,
          shouldExpandToCenter,
          isPinned,
          isGrowingOutOfIcon,
        })}
        style={
          {
            '--search-bar-center-offset': `${centerOffset}px`,
            '--search-bar-origin-offset': `${originOffset}px`,
            '--search-bar-origin-width': `${originWidth}px`,
          } as React.CSSProperties
        }
      >
        <SearchIconTransitionWrapper
          ref={tabletOpenButtonRef}
          $iconWidth={
            IconSize[isSearchInTabletMode && !isSearchBarExpanded ? 'S' : 'XS']
          }
          aria-label={t('Open search bar')}
          onClick={handleOnClickSearchIcon}
          data-testid="searchIcon"
          tabIndex={isSearchInTabletMode ? 0 : -1}
        >
          <StyledSearchIcon
            asset={GenericIconName.Search}
            size={isSearchInTabletMode && !isSearchBarExpanded ? 'S' : 'XS'}
          />
        </SearchIconTransitionWrapper>
        <StyledInput
          data-testid="searchBarInput"
          placeholder={t('Search for items, brands and more...')}
          ref={searchInputRef}
          onChange={e => {
            handleInputOnChangeWithDebounce(e.target.value);
            handleInputOnChangeTrackingWithDebounce(e.target.value);
          }}
          onFocus={onFocusSearchInput}
          data-is-focused={isInputFocused}
          tabIndex={isSearchInTabletMode && !isSearchBarExpanded ? -1 : 0}
        />
        <TrailingControls>
          <CloseIconTransitionWrapper
            data-opened={shouldShowCloseIcon}
            aria-label={t('Clear search bar')}
            data-testid="searchBarClearIcon"
            onClick={clearSearchAndFocus}
            tabIndex={shouldShowCloseIcon ? 0 : -1}
            onKeyDown={clearButtonOnKeyDownHandler}
          >
            <StyledClearText>{t('Clear')}</StyledClearText>
          </CloseIconTransitionWrapper>
        </TrailingControls>
        <ConditionalSearchSuggestionsHistory
          search={searchValue}
          handleRemoveInputFocus={handleRemoveInputFocus}
          isInputFocused={isInputFocused}
          createSearchPath={createSearchPath}
          enableRightArrow
          onClickSuggestion={onClickSuggestion}
          onNavigateSuggestionList={setSuggestionSelected}
        />
      </StyledSearchBarWrapper>
    </Wrapper>
  );
};

const StyledClearText = styled.span`
  font-size: ${FONT_SIZES.px12};
  color: ${COLORS['Text.Grey']};
`;

const StyledSearchBarWrapper = styled(SearchBarWrapper)`
  &[data-is-focused='true'] {
    border-radius: 2px 2px 0 0;
  }
`;

const StyledInput = styled(SearchInput)`
  margin-left: 8px;
  font-size: clamp(${FONT_SIZES.px11}, 1vw, ${FONT_SIZES.px14});
`;

const SEARCH_ICON_MARGIN_LEFT = 8;

const StyledSearchIcon = styled(SearchIcon)`
  margin-left: ${SEARCH_ICON_MARGIN_LEFT}px;
  // the icon needs to be 18x18 pixels to be 1:1 with the design
  // https://www.figma.com/file/78aeShVkIg8jpHIdts5ufT/DT-4747---Search---Auto-Suggest?node-id=968%3A39892&mode=dev
  width: 18px;
  height: 18px;
`;

// The tablet bar is absolutely positioned inside the full width navigation
// row, so `right` alone centres it in the viewport. The desktop bar sits in the
// flex row instead, which is why that one rides on a measured translation.
const SearchWrapperExpandedCss = css`
  &&& {
    width: ${SEARCH_BAR_FOCUSED_WIDTH}px;
    height: ${SEARCH_BAR_HEIGHT}px;
    right: calc(50% - ${SEARCH_BAR_FOCUSED_WIDTH / 2}px);
  }
`;

const SearchWrapperClosedCss = css`
  &&& {
    width: ${SEARCH_BAR_TABLET_WIDTH}px;
    overflow: hidden;
    border: none;
    height: 36px;
  }

  ${StyledSearchIcon} {
    position: relative;
    left: 50%;
    transform: translate(-${HEADER_DEFAULT_PADDING.tablet / 2}px);
  }

  ${StyledInput} {
    display: none;
  }
`;

const SearchBarWrapperDesktop = css`
  &&& {
    position: relative;
    top: unset;
    right: unset;
    width: 100%;
    border-color: ${COLORS.Grey4};
    background: ${COLORS.Grey4};
    // eslint-disable-next-line local-rules/css-layout-animation
    transition: transform ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in,
      width ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in,
      box-shadow ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in;
  }

  ${StyledSearchIcon} {
    position: relative;
    left: unset;
    transform: unset;
  }

  ${StyledInput} {
    display: block;
  }
`;

const OnInputFocusAnimationCss = css`
  &&& {
    box-shadow: -27px 0 16px -2px ${COLORS.White};
    background-color: ${COLORS.White};
    border: 1px solid ${COLORS['Outline.Grey']};

    ${StyledInput} {
      background-color: ${COLORS.White};
    }

    ${StyledCloseIcon} {
      background-color: ${COLORS.White};
    }
  }
`;

const FocusGrowInPlaceCss = css`
  &&& {
    transform: translateX(-15%);
    width: 115%;
  }
`;

const ExpandToCenterCss = css`
  @media ${mediaLargeScreens} {
    &&& {
      width: 100%;
    }
  }
`;

const FocusCenteredCss = css`
  &&& {
    // eslint-disable-next-line local-rules/css-layout-animation
    transform: translateX(var(--search-bar-center-offset, 0px));
    width: ${SEARCH_BAR_FOCUSED_WIDTH}px;
  }
`;

// Once the nav row has scrolled past, the open bar detaches and rides along at
// the top. Its own transform still supplies the horizontal centering, and the
// elevation from SearchBarFlyoutHostCss keeps it over the sticky header row.
const SearchBarPinnedCss = css`
  &&& {
    position: fixed;
    top: ${SEARCH_BAR_PINNED_TOP}px;
  }
`;

// The open bar hosts the suggestions flyout: its transform makes it the
// flyout's stacking context, so it has to out-stack the page overlay, and it
// must not clip the flyout with the overflow it carries while collapsed.
const SearchBarFlyoutHostCss = css`
  &&& {
    z-index: ${Z_INDEXES.TADARIDA_HEADER_SEARCH_RESULTS};
    overflow: visible;
  }
`;

// Opened from the sticky header, the bar starts on the search icon it replaces
// and grows from there, instead of sweeping in from the right end of the
// navigation row where it actually lives.
const GrowOutOfIconCss = css`
  &&& {
    animation: growOutOfIcon ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in;

    // The bar is wider than its content while it is still icon sized, so it
    // fades in over the first frames rather than showing that overflow.
    // eslint-disable-next-line local-rules/css-layout-animation
    @keyframes growOutOfIcon {
      from {
        transform: translateX(var(--search-bar-origin-offset, 0px));
        width: var(--search-bar-origin-width, ${SEARCH_BAR_HEIGHT}px);
        opacity: 0;
      }
    }
  }
`;

const getSearchBarModifierClasses = ({
  isSearchBarExpanded,
  isSearchInTabletMode,
  isInputFocused,
  isSearchBarExtended,
  canExpandToCenter,
  shouldExpandToCenter,
  isPinned,
  isGrowingOutOfIcon,
}: {
  isSearchBarExpanded: boolean;
  isSearchInTabletMode: boolean;
  isInputFocused: boolean;
  isSearchBarExtended: boolean;
  canExpandToCenter: boolean;
  shouldExpandToCenter: boolean;
  isPinned: boolean;
  isGrowingOutOfIcon: boolean;
}) => {
  const isDesktopFocused = !isSearchInTabletMode && isInputFocused;

  return cx(
    isSearchBarExpanded ? SearchWrapperExpandedCss : SearchWrapperClosedCss,
    isSearchInTabletMode ? SearchBarWrapperTablet : SearchBarWrapperDesktop,
    isSearchBarExtended && SearchBarFlyoutHostCss,
    isSearchBarExtended && OnInputFocusAnimationCss,
    canExpandToCenter && !isSearchInTabletMode && ExpandToCenterCss,
    isDesktopFocused &&
      (shouldExpandToCenter ? FocusCenteredCss : FocusGrowInPlaceCss),
    isPinned && SearchBarPinnedCss,
    isGrowingOutOfIcon && GrowOutOfIconCss,
  );
};

const SearchIconTransitionWrapper = styled(UnstyledButton)<{
  $iconWidth: number;
}>`
  width: ${({$iconWidth}) => $iconWidth + SEARCH_ICON_MARGIN_LEFT}px;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: width ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in;
`;

// Both buttons share one grid cell so swapping them is a pure cross fade.
// Toggling their width instead made the input jump, as neither 0 nor
// fit-content can be interpolated.
const TrailingControls = styled.div`
  display: grid;
  align-items: center;
  justify-items: end;
  margin-right: ${SPACING['X.Md']};
`;

const CloseIconTransitionWrapper = styled(UnstyledButton)`
  grid-area: 1 / 1;
  opacity: 0;
  pointer-events: none;
  transition: opacity ${SEARCH_BAR_ANIMATION_TIME_MS}ms ease-in;

  &[data-opened='true'] {
    opacity: 1;
    pointer-events: auto;
  }
`;
